/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, useEffect, useState } from "react";
import PropTypes from "prop-types";

const ThemeContext = createContext();

export function ThemeProvider({ children }) {
  const [theme, setTheme] = useState("light");
  const [isTransitioning, setIsTransitioning] = useState(false);

  // Initialize theme after component mounts
  useEffect(() => {
    try {
      // Check localStorage first
      const stored = localStorage.getItem("theme");
      if (stored) {
        setTheme(stored);
        return;
      }

      // Then check system preference
      if (
        window.matchMedia &&
        window.matchMedia("(prefers-color-scheme: dark)").matches
      ) {
        setTheme("dark");
      }
    } catch (error) {
      console.warn("Failed to get theme preference:", error);
    }
  }, []);

  // Apply theme change
  useEffect(() => {
    const root = window.document.documentElement;

    // Start transition
    setIsTransitioning(true);
    root.classList.add("transitioning-theme");

    // Remove old theme and add new theme
    root.classList.remove("light", "dark");
    root.classList.add(theme);

    // Store preference
    try {
      localStorage.setItem("theme", theme);
    } catch (err) {
      console.warn("Could not save theme to localStorage:", err);
    }

    // End transition after a brief delay
    const timer = setTimeout(() => {
      setIsTransitioning(false);
      root.classList.remove("transitioning-theme");
    }, 200);

    return () => clearTimeout(timer);
  }, [theme]);

  const toggleTheme = () => {
    setTheme((prev) => (prev === "light" ? "dark" : "light"));
  };

  const value = {
    theme,
    setTheme,
    toggleTheme,
    isTransitioning,
  };

  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
}

ThemeProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export function useTheme() {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
}
